/**
 * ET Header
 * - "Shop all" mega menu: opens on hover and toggles on click (hover mode),
 *   or toggles on click only (click mode). Closed on Escape / outside click.
 * - Mobile drawer toggle.
 * - Sticky shadow on scroll.
 */
if (!customElements.get('et-header')) {
  class ETHeader extends HTMLElement {
    constructor() {
      super();
      this.trigger = this.dataset.trigger || 'hover';
      this.closeTimer = null;
      this.onDocClick = this.onDocClick.bind(this);
      this.onKeyup = this.onKeyup.bind(this);
      this.onScroll = this.onScroll.bind(this);
    }

    connectedCallback() {
      this.shopAll = this.querySelector('.et-shop-all');
      this.megaMenu = this.querySelector('.et-mega-menu');
      this.mobileToggle = this.querySelector('.et-mobile-toggle');
      this.mobileMenu = this.querySelector('.et-mobile-menu');
      this.overlay = this.querySelector('.et-header__overlay');

      this.bindMegaMenu();
      this.bindMobileMenu();

      document.addEventListener('keyup', this.onKeyup);
      document.addEventListener('click', this.onDocClick);

      if (this.classList.contains('et-header-sticky--active')) {
        window.addEventListener('scroll', this.onScroll, { passive: true });
        this.onScroll();
      }
    }

    disconnectedCallback() {
      document.removeEventListener('keyup', this.onKeyup);
      document.removeEventListener('click', this.onDocClick);
      window.removeEventListener('scroll', this.onScroll);
    }

    /* ---------------- Mega menu ---------------- */
    bindMegaMenu() {
      if (!this.shopAll || !this.megaMenu) return;
      this.megaMenu.hidden = false;

      // Click always toggles.
      this.shopAll.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.toggleMega();
      });

      this.bindCategories();

      // Hover mode: open on enter, close (debounced) on leave of trigger + panel.
      if (this.trigger === 'hover') {
        const enter = () => {
          clearTimeout(this.closeTimer);
          this.openMega();
        };
        const leave = () => {
          clearTimeout(this.closeTimer);
          this.closeTimer = setTimeout(() => this.closeMega(), 180);
        };
        [this.shopAll, this.megaMenu].forEach((el) => {
          el.addEventListener('mouseenter', enter);
          el.addEventListener('mouseleave', leave);
        });
      }
    }

    // Left-rail category tabs swap the visible panel.
    bindCategories() {
      this.cats = Array.from(this.megaMenu.querySelectorAll('[data-mega-cat]'));
      this.panels = Array.from(this.megaMenu.querySelectorAll('[data-mega-panel]'));
      if (!this.cats.length) return;

      this.cats.forEach((cat) => {
        const activate = () => this.selectCategory(cat.dataset.megaCat);
        cat.addEventListener('mouseenter', activate);
        cat.addEventListener('focus', activate);
        // Click follows the link (native <a> navigation); hover still swaps panels.
      });
    }

    selectCategory(id) {
      this.cats.forEach((cat) => {
        const active = cat.dataset.megaCat === id;
        cat.classList.toggle('is-active', active);
        cat.setAttribute('aria-selected', active ? 'true' : 'false');
      });
      this.panels.forEach((panel) => {
        const active = panel.dataset.megaPanel === id;
        panel.classList.toggle('is-active', active);
        panel.hidden = !active;
      });
    }

    openMega() {
      this.classList.add('et-mega-open');
      this.shopAll.setAttribute('aria-expanded', 'true');
    }

    closeMega() {
      this.classList.remove('et-mega-open');
      this.shopAll.setAttribute('aria-expanded', 'false');
    }

    toggleMega() {
      clearTimeout(this.closeTimer);
      if (this.classList.contains('et-mega-open')) {
        this.closeMega();
      } else {
        this.openMega();
      }
    }

    /* ---------------- Mobile drawer ---------------- */
    bindMobileMenu() {
      if (!this.mobileToggle || !this.mobileMenu) return;

      this.mobileToggle.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.toggleMobile();
      });

      if (this.overlay) {
        this.overlay.addEventListener('click', () => this.closeMobile());
      }

      this.mmPanels = Array.from(this.mobileMenu.querySelectorAll('[data-mm-panel]'));
      // JS takes over visibility via transforms — drop the no-JS fallback attr.
      this.mmPanels.forEach((panel) => {
        panel.hidden = false;
      });

      // Drill into a category panel.
      this.mobileMenu.querySelectorAll('[data-mm-drill]').forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          this.showMobilePanel(btn.dataset.mmDrill);
        });
      });

      // Back to the root panel.
      this.mobileMenu.querySelectorAll('[data-mm-back]').forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          this.showMobilePanel('root');
        });
      });

      // Close from any panel header.
      this.mobileMenu.querySelectorAll('[data-mm-close]').forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          this.closeMobile();
        });
      });
    }

    showMobilePanel(id) {
      if (!this.mmPanels) return;
      this.mmPanels.forEach((panel) => {
        panel.classList.toggle('is-active', panel.dataset.mmPanel === id);
      });
    }

    openMobile() {
      this.classList.add('et-mobile-open');
      this.mobileToggle.setAttribute('aria-expanded', 'true');
      this.mobileMenu.setAttribute('aria-hidden', 'false');
      document.body.classList.add('overflow-hidden');
    }

    closeMobile() {
      this.classList.remove('et-mobile-open');
      this.mobileToggle.setAttribute('aria-expanded', 'false');
      this.mobileMenu.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('overflow-hidden');
      // Reset to the root panel once the drawer has slid out.
      setTimeout(() => this.showMobilePanel('root'), 300);
    }

    toggleMobile() {
      if (this.classList.contains('et-mobile-open')) {
        this.closeMobile();
      } else {
        this.openMobile();
      }
    }

    /* ---------------- Global handlers ---------------- */
    onKeyup(e) {
      if (e.code && e.code.toUpperCase() === 'ESCAPE') {
        this.closeMega();
        this.closeMobile();
      }
    }

    onDocClick(e) {
      if (
        this.classList.contains('et-mega-open') &&
        !this.megaMenu.contains(e.target) &&
        !this.shopAll.contains(e.target)
      ) {
        this.closeMega();
      }
    }

    onScroll() {
      this.classList.toggle('et-stuck', window.scrollY > 4);
    }
  }

  customElements.define('et-header', ETHeader);
}
